import React, { useEffect, useState } from "react";
import RoleSelect from "./pages/RoleSelect";
import SellerWizard, { INITIAL_DATA } from "./pages/SellerWizard";
import BuyerWizard from "./pages/BuyerWizard";
import BuyerFirstWizard from "./pages/BuyerFirstWizard";
import BuyerFirstSuccess from "./pages/BuyerFirstSuccess";
import SellerCompleteWizard from "./pages/SellerCompleteWizard";
import SellerSuccess from "./pages/SellerSuccess";
import ContractPreview from "./pages/ContractPreview";
import VehicleInspectionForm from "./pages/VehicleInspectionForm";
import VehicleInspectionSheet from "./pages/VehicleInspectionSheet";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PanelLayout from "./pages/panel/PanelLayout";
import { getContractById } from "./api/contractsApi";
import { getCurrentUser } from "./api/authApi";

const STORAGE_KEY = "qolnameh_seller_draft";

// /contract/:id  → خریدار دوم (buyer completes)
// /seller-contract/:id → فروشنده دوم (seller completes)
function getRouteFromUrl() {
  const buyerMatch = window.location.pathname.match(
    /^\/contract\/([a-zA-Z0-9]+)/,
  );
  if (buyerMatch) return { type: "buyer-complete", id: buyerMatch[1] };

  const sellerMatch = window.location.pathname.match(
    /^\/seller-contract\/([a-zA-Z0-9]+)/,
  );
  if (sellerMatch) return { type: "seller-complete", id: sellerMatch[1] };

  return { type: null, id: null };
}

function getTokenFromCookie() {
  const match = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
  return match ? match[1] : null;
}

const urlRoute = getRouteFromUrl();

export default function App() {
  const [view, setView] = useState("panel");
  const [finalData, setFinalData] = useState(INITIAL_DATA);
  const [contractId, setContractId] = useState(null);
  const [vehicleInspectionResult, setVehicleInspectionResult] = useState(null);
  const [buyerMobile, setBuyerMobile] = useState("");
  const [sellerMobile, setSellerMobile] = useState("");

  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authView, setAuthView] = useState("login");
  const [currentUser, setCurrentUser] = useState(null);
  const [limitError, setLimitError] = useState("");

  // مشاهده یک قولنامه قبلی از داخل پنل
  const [viewedContract, setViewedContract] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractError, setContractError] = useState("");

  useEffect(() => {
    // لینک‌های خریدار/فروشنده دوم نیازی به لاگین ندارند
    if (urlRoute.type === "buyer-complete") {
      setContractId(urlRoute.id);
      setView("buyer");
      setAuthChecked(true);
      return;
    }

    if (urlRoute.type === "seller-complete") {
      setContractId(urlRoute.id);
      setView("sellerComplete");
      setAuthChecked(true);
      return;
    }

    const token = getTokenFromCookie();
    if (!token) {
      setAuthChecked(true);
      setIsLoggedIn(false);
      return;
    }

    getCurrentUser()
      .then((user) => {
        setCurrentUser(user);
        setIsLoggedIn(true);
      })
      .catch(() => {
        document.cookie = "token=; path=/; max-age=0";
        setCurrentUser(null);
        setIsLoggedIn(false);
      })
      .finally(() => {
        setAuthChecked(true);
      });
  }, []);

  const handleLoginSuccess = () => {
    getCurrentUser()
      .then((user) => {
        setCurrentUser(user);
        setIsLoggedIn(true);
      })
      .catch(() => {
        document.cookie = "token=; path=/; max-age=0";
        setCurrentUser(null);
        setIsLoggedIn(false);
      });
  };

  const handleLogout = () => {
    document.cookie = "token=; path=/; max-age=0";
    setCurrentUser(null);
    setIsLoggedIn(false);
    setView("panel");
  };

  const hasNoAgreementLimit = Number(currentUser?.agreement_limit) === 0;

  const handleStartNewContract = () => {
    if (hasNoAgreementLimit) {
      setLimitError("سقف ثبت قولنامه شما تمام شده است.");
      return;
    }

    setLimitError("");
    setView("role");
  };

  const handleBackToPanel = () => {
    setViewedContract(null);
    setContractError("");
    window.history.pushState({}, "", "/");
    setView("panel");
  };

  // مشاهده یک قولنامه قبلی از لیست پنل: فقط دیتا از API گرفته و به ContractPreview داده می‌شود

  const handleViewContract = (id) => {
    setContractLoading(true);
    setContractError("");

    getContractById(id)
      .then((data) => {
        setViewedContract(data);
        window.location.href = `/contract/${id}`;
      })
      .catch((err) => setContractError(err.message))
      .finally(() => setContractLoading(false));
  };

  const handleRoleSelect = (role) => {
    if (role === "seller") setView("seller");
    if (role === "buyer") setView("buyer");
    if (role === "buyerFirst") setView("buyerFirst");
    if (role === "singlePerson") setView("singlePerson");
    if (role === "vehicleInspection") setView("vehicleInspection");
  };

  // فروشنده اول اقدام کرده
  const handleSellerComplete = (data) => {
    setFinalData(data);
    setContractId(data.contractId);
    setBuyerMobile(data.buyerMobile);
    setView("sellerSuccess");
  };

  // خریدار اول اقدام کرده
  const handleBuyerFirstComplete = (data) => {
    setFinalData(data);
    setContractId(data.contractId);
    setSellerMobile(data.sellerMobile);
    setView("buyerFirstSuccess");
  };

  // خریدار دوم (از لینک) تکمیل کرده
  const handleBuyerComplete = (data) => {
    setFinalData(data);
    setView("preview");
  };

  // فروشنده دوم (از لینک) تکمیل کرده
  const handleSellerCompleteWizard = (data) => {
    setFinalData(data);
    setView("preview");
  };

  const handleRestart = () => {
    localStorage.removeItem(STORAGE_KEY);
    if (hasNoAgreementLimit) {
      setView("panel");
      setLimitError("سقف ثبت قولنامه شما تمام شده است.");
      window.history.pushState({}, "", "/");
      return;
    }

    setFinalData(INITIAL_DATA);
    setContractId(null);
    setBuyerMobile("");
    setSellerMobile("");
    setView("role");
    window.history.pushState({}, "", "/");
  };

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-sky border-t-transparent" />
          <p className="text-sm text-gray-400">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  // لینک‌های عمومی نیازی به لاگین ندارند
  if (!isLoggedIn && !urlRoute.type) {
    if (authView === "register") {
      return (
        <Register
          onRegisterSuccess={handleLoginSuccess}
          onShowLogin={() => setAuthView("login")}
        />
      );
    }

    return (
      <Login
        onLoginSuccess={handleLoginSuccess}
        onShowRegister={() => setAuthView("register")}
      />
    );
  }

  // پنل کاربری: پروفایل، مالی، قولنامه‌های قبلی
  if (view === "panel") {
    return (
      <PanelLayout
        onStartNewContract={handleStartNewContract}
        onViewContract={handleViewContract}
        onLogout={handleLogout}
        userName={currentUser?.name}
        canCreateAgreement={!hasNoAgreementLimit}
        limitError={limitError}
      />
    );
  }

  // مشاهده یک قولنامه قبلی (از داخل پنل)
  if (view === "contractView") {
    if (contractLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-surface">
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-sky border-t-transparent" />
            <p className="text-sm text-gray-400">در حال بارگذاری قولنامه...</p>
          </div>
        </div>
      );
    }
    if (contractError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface px-4 text-center">
          <p className="text-sm font-bold text-red-600">{contractError}</p>
          <button
            onClick={handleBackToPanel}
            className="rounded-xl bg-navy px-5 py-2.5 text-sm font-bold text-white transition hover:bg-navy-light"
          >
            بازگشت به پنل
          </button>
        </div>
      );
    }
    return (
      <ContractPreview
        data={viewedContract}
        onEdit={handleBackToPanel}
        onRestart={handleBackToPanel}
      />
    );
  }

  // فروشنده دوم (از لینک)
  if (view === "sellerComplete") {
    return (
      <SellerCompleteWizard
        contractId={contractId}
        onComplete={handleSellerCompleteWizard}
      />
    );
  }

  // فروشنده اول
  if (view === "seller") {
    return (
      <SellerWizard
        onComplete={handleSellerComplete}
        onBackToRoles={() => setView("role")}
      />
    );
  }

  if (view === "singlePerson") {
    return (
      <SellerWizard
        flowType="single-person"
        onComplete={handleSellerComplete}
        onBackToRoles={() => setView("role")}
      />
    );
  }

  // بازرسی کارشناسی خودرو (یک‌نفره)
  if (view === "vehicleInspection") {
    return (
      <VehicleInspectionForm
        onSuccess={(created) => {
          setVehicleInspectionResult(created);
          setView("vehicleInspectionSuccess");
        }}
        onBack={() => setView("role")}
      />
    );
  }

  if (view === "vehicleInspectionSuccess") {
    return (
      <VehicleInspectionSheet
        inspection={vehicleInspectionResult}
        onRegisterAnother={() => setView("vehicleInspection")}
        onGoToPanel={handleBackToPanel}
      />
    );
  }

  if (view === "sellerSuccess") {
    return (
      <SellerSuccess
        contractId={contractId}
        buyerMobile={buyerMobile}
        isSinglePersonMode={finalData?.flowType === "single-person"}
        onRestart={handleRestart}
        onGoToPanel={handleBackToPanel}
      />
    );
  }

  // خریدار اول
  if (view === "buyerFirst") {
    return (
      <BuyerFirstWizard
        onComplete={handleBuyerFirstComplete}
        onBackToRoles={() => setView("role")}
      />
    );
  }

  if (view === "buyerFirstSuccess") {
    return (
      <BuyerFirstSuccess
        contractId={contractId}
        sellerMobile={sellerMobile}
        onRestart={handleRestart}
        onGoToPanel={handleBackToPanel}
      />
    );
  }

  // خریدار دوم (از لینک)
  if (view === "buyer") {
    return (
      <BuyerWizard
        contractId={contractId}
        onComplete={handleBuyerComplete}
        onBackToRoles={() => {
          window.history.pushState({}, "", "/");
          setView("role");
        }}
      />
    );
  }

  if (view === "preview") {
    return (
      <ContractPreview
        data={finalData}
        onEdit={() =>
          setView(
            finalData?.flowType === "single-person" ? "singlePerson" : "buyer",
          )
        }
        onRestart={handleRestart}
      />
    );
  }

  return <RoleSelect onSelect={handleRoleSelect} onBack={handleBackToPanel} />;
}
